import { useCallback, useEffect, useState } from "react";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import useListingStats from "../../data/useListingStats";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import { IPart } from "../../models/Part";
import LoadingWheel from "../../components/shared/LoadingWheel";
import CategoriesToWork from "../../components/ebayOptimisation/CategoriesToWork";
import GroupsToWork from "../../components/ebayOptimisation/GroupsToWork";
import EbayOptimisationQueue from "../../components/ebayOptimisation/EbayOptimisationQueue";
import OptimisationPageHeader from "../../components/ebayOptimisation/OptimisationPageHeader";
import TriageTiles from "../../components/ebayOptimisation/TriageTiles";
import { IQueueFilters, defaultQueueFilters } from "../../components/ebayOptimisation/queueFilterState";

/**
 * The eBay Optimisation triage queue: listings that are not selling, oldest first by the date
 * they were FIRST listed. Review each one, stage what you change, then send them to be relisted.
 * Everything stages except grade, photos, Send to QC and Scrap out, which act on the part now.
 */
export default function EbayOptimisation() {
  const { setInfo } = usePageNameContext();
  const { features } = useCustomerDetails();
  const optimisationEnabled = useFeatureEnabled("eBay Optimisation");
  const { listingStats } = useListingStats();

  const [filters, setFilters] = useState<IQueueFilters>(defaultQueueFilters);
  const [pageRows, setPageRows] = useState<IPart[]>([]);

  const updateFilters = useCallback((next: Partial<IQueueFilters>) => setFilters((current) => ({ ...current, ...next })), []);
  const clearFilters = useCallback(() => setFilters(defaultQueueFilters), []);

  useEffect(() => {
    setInfo({
      name: "eBay Optimisation",
      desc: "Listings that are not selling, oldest first. Review, stage, then relist.",
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (features.isLoading) {
    return <LoadingWheel />;
  }

  if (!optimisationEnabled) {
    return (
      <div className="mt-8 rounded-md border border-gray-200 bg-white p-6 text-center">
        <h2 className="text-lg font-medium text-gray-900">eBay Optimisation is not enabled</h2>
        <p className="mt-2 text-sm text-gray-600">
          This feature is not enabled for your account. Contact support if you'd like it turned on.
        </p>
      </div>
    );
  }

  const stats = listingStats.data?.data;

  return (
    <div className="mt-4 flex flex-col gap-4">
      <OptimisationPageHeader rows={pageRows} />

      {/* One overview card: the age split on the left, the categories to work on the right */}
      <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] xl:divide-x xl:divide-gray-200">
        <div className="min-w-0 border-b border-gray-200 xl:border-b-0">
          <TriageTiles stats={stats} isLoading={listingStats.isLoading} filters={filters} onChange={updateFilters} />
        </div>
        <div className="min-w-0">
          <CategoriesToWork
            stats={stats}
            isLoading={listingStats.isLoading}
            activeCategoryId={filters.categoryId}
            onShow={(categoryId, categoryName) => updateFilters({ categoryId, categoryName })}
          />
        </div>
      </div>

      <GroupsToWork stats={stats} isLoading={listingStats.isLoading} filters={filters} onChange={updateFilters} />

      <EbayOptimisationQueue filters={filters} onFiltersChange={updateFilters} onClearFilters={clearFilters} onRowsChange={setPageRows} />
    </div>
  );
}
